import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BellIcon, CheckIcon, InformationCircleIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import LoadingSpinner from '../common/LoadingSpinner';
import { useUI } from '../../contexts/UIContext';

/**
 * Notification Settings Component for Stakeholder Groups
 * Allows configuration of when and how stakeholder group members are notified
 */
const NotificationSettings = ({ group, onUpdate }) => {
  const { showNotification } = useUI();
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  // Initialize preferences from group data or use defaults
  useEffect(() => {
    if (group) {
      const defaultPrefs = {
        channels: {
          in_app: true,
          email: true
        },
        notify_on: {
          issue_created: true,
          issue_severity_increased: true,
          issue_resolved: false,
          schema_change: true,
          validation_failure: true,
          data_quality_degradation: true
        },
        frequency: 'immediate',
        min_severity: 'medium',
        notify_only_critical_impact: false,
        quiet_hours: {
          enabled: false,
          start_time: '22:00',
          end_time: '08:00',
          timezone: 'UTC',
          emergency_override: true
        }
      };

      // Check if notification_preferences exists and has valid structure
      const hasValidPreferences = group.notification_preferences &&
        group.notification_preferences.channels &&
        group.notification_preferences.notify_on;

      setPreferences(hasValidPreferences ? group.notification_preferences : defaultPrefs);
    }
  }, [group]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await onUpdate(preferences);
      setShowSuccess(true);
      showNotification('Notification preferences saved successfully', 'success');
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      showNotification(`Failed to save preferences: ${error.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const updatePreference = (path, value) => {
    setPreferences(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current = updated;

      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  if (!preferences) {
    return (
      <div className="flex justify-center py-4">
        <LoadingSpinner size="sm" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-secondary-200">
      <div className="px-6 py-4 border-b border-secondary-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BellIcon className="h-5 w-5 text-purple-500" />
            <h2 className="text-lg font-semibold text-secondary-900">Group Notification Rules</h2>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn btn-primary btn-sm"
          >
            {saving ? (
              <>
                <LoadingSpinner size="sm" />
                Saving...
              </>
            ) : showSuccess ? (
              <>
                <CheckIcon className="h-4 w-4 mr-1" />
                Saved!
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
        <p className="text-sm text-secondary-600 mt-1">
          Configure <strong>when</strong> this group gets notified and <strong>who</strong> receives alerts based on affected data objects
        </p>

        {/* Info Callout */}
        <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex">
            <InformationCircleIcon className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="ml-2">
              <p className="text-xs text-blue-700">
                These rules apply <em>when data objects assigned to this group</em> are affected by issues.
                Organization-wide event triggers are controlled in{' '}
                <Link to="/admin/notifications" className="inline-flex items-center font-medium text-blue-800 hover:text-blue-900 underline">
                  Organization Settings
                  <ArrowTopRightOnSquareIcon className="h-3 w-3 ml-0.5" />
                </Link>
                . Individual members can adjust their frequency in their personal preferences.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Notification Channels */}
        <div>
          <h3 className="text-sm font-semibold text-secondary-900 mb-3">
            <span className="text-purple-600">HOW to Notify:</span> Channels
          </h3>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.channels.in_app}
                onChange={(e) => updatePreference('channels.in_app', e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="ml-2 text-sm text-secondary-700">In-app notifications</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.channels.email}
                onChange={(e) => updatePreference('channels.email', e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="ml-2 text-sm text-secondary-700">Email notifications</span>
            </label>
          </div>
        </div>

        {/* Notification Types */}
        <div>
          <h3 className="text-sm font-semibold text-secondary-900 mb-3">
            <span className="text-purple-600">WHEN to Notify:</span> Event Types
          </h3>
          <div className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.notify_on.issue_created}
                onChange={(e) => updatePreference('notify_on.issue_created', e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="ml-2 text-sm text-secondary-700">New issues created</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.notify_on.issue_severity_increased}
                onChange={(e) => updatePreference('notify_on.issue_severity_increased', e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="ml-2 text-sm text-secondary-700">Issue severity increased</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.notify_on.issue_resolved}
                onChange={(e) => updatePreference('notify_on.issue_resolved', e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="ml-2 text-sm text-secondary-700">Issues resolved</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.notify_on.schema_change}
                onChange={(e) => updatePreference('notify_on.schema_change', e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="ml-2 text-sm text-secondary-700">Schema changes</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.notify_on.validation_failure}
                onChange={(e) => updatePreference('notify_on.validation_failure', e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="ml-2 text-sm text-secondary-700">Validation failures</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.notify_on.data_quality_degradation}
                onChange={(e) => updatePreference('notify_on.data_quality_degradation', e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="ml-2 text-sm text-secondary-700">Data quality degradation</span>
            </label>
          </div>
        </div>

        {/* Frequency */}
        <div>
          <h3 className="text-sm font-semibold text-secondary-900 mb-3">
            <span className="text-purple-600">WHEN to Notify:</span> Frequency
          </h3>
          <select
            value={preferences.frequency}
            onChange={(e) => updatePreference('frequency', e.target.value)}
            className="select select-bordered select-sm bg-white text-secondary-900 w-full max-w-xs"
          >
            <option value="immediate">Immediate</option>
            <option value="daily_digest">Daily Digest</option>
            <option value="weekly_digest">Weekly Digest</option>
          </select>
          <p className="text-xs text-secondary-500 mt-1">
            {preferences.frequency === 'immediate' && 'Notifications sent as soon as issues occur'}
            {preferences.frequency === 'daily_digest' && 'Consolidated daily summary of all issues'}
            {preferences.frequency === 'weekly_digest' && 'Weekly summary delivered every Monday'}
          </p>
        </div>

        {/* Minimum Severity */}
        {preferences.frequency === 'immediate' && (
          <div>
            <h3 className="text-sm font-semibold text-secondary-900 mb-3">
              <span className="text-purple-600">WHEN to Notify:</span> Minimum Severity
            </h3>
            <select
              value={preferences.min_severity}
              onChange={(e) => updatePreference('min_severity', e.target.value)}
              className="select select-bordered select-sm bg-white text-secondary-900 w-full max-w-xs"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical only</option>
            </select>
            <p className="text-xs text-secondary-500 mt-1">
              Only notify for issues at or above this severity level
            </p>
          </div>
        )}

        {/* Critical Impact Only */}
        <div>
          <label className="flex items-start">
            <input
              type="checkbox"
              checked={preferences.notify_only_critical_impact}
              onChange={(e) => updatePreference('notify_only_critical_impact', e.target.checked)}
              className="checkbox checkbox-primary checkbox-sm mt-1"
            />
            <div className="ml-2">
              <span className="text-sm font-medium text-secondary-900">Critical impact only</span>
              <p className="text-xs text-secondary-500">
                Only notify when business impact level is marked as critical
              </p>
            </div>
          </label>
        </div>

        {/* Quiet Hours */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-secondary-900">Quiet Hours</h3>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={preferences.quiet_hours.enabled}
                onChange={(e) => updatePreference('quiet_hours.enabled', e.target.checked)}
                className="checkbox checkbox-primary checkbox-sm"
              />
              <span className="ml-2 text-sm text-secondary-700">Enable</span>
            </label>
          </div>

          {preferences.quiet_hours.enabled && (
            <div className="bg-secondary-50 p-4 rounded space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">
                    <span className="label-text text-xs">Start Time</span>
                  </label>
                  <input
                    type="time"
                    value={preferences.quiet_hours.start_time}
                    onChange={(e) => updatePreference('quiet_hours.start_time', e.target.value)}
                    className="input input-bordered input-sm bg-white text-secondary-900 w-full"
                  />
                </div>
                <div>
                  <label className="label">
                    <span className="label-text text-xs">End Time</span>
                  </label>
                  <input
                    type="time"
                    value={preferences.quiet_hours.end_time}
                    onChange={(e) => updatePreference('quiet_hours.end_time', e.target.value)}
                    className="input input-bordered input-sm bg-white text-secondary-900 w-full"
                  />
                </div>
              </div>

              <label className="flex items-start">
                <input
                  type="checkbox"
                  checked={preferences.quiet_hours.emergency_override}
                  onChange={(e) => updatePreference('quiet_hours.emergency_override', e.target.checked)}
                  className="checkbox checkbox-primary checkbox-sm mt-1"
                />
                <div className="ml-2">
                  <span className="text-xs font-medium text-secondary-900">Override for critical issues</span>
                  <p className="text-xs text-secondary-500">
                    Critical severity issues bypass quiet hours
                  </p>
                </div>
              </label>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
